/** The overview's rows: one per pile with its change, and the totals (pure, tested). */
import type { ChangeOut, PileObsOut, VolumeOut } from "../../api/generated/model";
import { type ArcScale, positionM } from "../../lib/units";

export interface PileRow {
  code: string;
  label: string | null;
  product: string | null;
  lifecycle: string;
  vBest: number | null;
  vMin: number | null;
  vMax: number | null;
  tonnes: number | null;
  tonnesSigma: number | null;
  stacked: number;
  reclaimed: number;
  observedFrac: number;
  positionFromM: number;
  positionToM: number;
  flags: string;
}

export const FLAGS: Record<string, string> = {
  E: "estimated: more than 5 % not seen",
  C: "carried forward: not seen this sweep",
  B: "bounds only",
  D: "density set by an operator",
  N: "new pile",
};

/** The flags' short names (badges). */
export const FLAG_LABEL: Record<string, string> = {
  E: "Estimated",
  C: "Carried",
  B: "Bounds",
  D: "Density set",
  N: "New",
};

export function flagsOf(v: VolumeOut | null, lifecycle: string): string {
  return [
    v?.is_estimated ? "E" : "",
    v?.carried_forward ? "C" : "",
    v?.bounds_only ? "B" : "",
    v?.density_source === "override" ? "D" : "",
    lifecycle === "new" ? "N" : "",
  ].join("");
}

export function pileRows(piles: PileObsOut[], changes: ChangeOut[], scale: ArcScale): PileRow[] {
  const ch = new Map(changes.filter((c) => c.scope === "pile").map((c) => [c.scope_id, c]));
  return piles
    .map((p) => {
      const c = ch.get(p.code);
      // a toe a little before the arc start would wrap to almost a full circle: it is at 0 m
      const near = (m: number) => (m > positionM(scale, scale.startBearingDeg - scale.direction * 30) ? 0 : m);
      const a = near(positionM(scale, p.slew_min_deg));
      const b = near(positionM(scale, p.slew_max_deg));
      return {
        code: p.code,
        label: p.label,
        product: p.product,
        lifecycle: p.lifecycle,
        vBest: p.volume?.v_best_m3 ?? null,
        vMin: p.volume?.v_min_m3 ?? null,
        vMax: p.volume?.v_max_m3 ?? null,
        tonnes: p.volume?.tonnes_t ?? null,
        tonnesSigma: p.volume?.tonnes_sigma_t ?? null,
        stacked: c?.dv_stacked_m3 ?? 0,
        reclaimed: c?.dv_reclaimed_m3 ?? 0,
        observedFrac: p.observed_frac,
        positionFromM: Math.min(a, b),
        positionToM: Math.max(a, b),
        flags: flagsOf(p.volume, p.lifecycle),
      };
    })
    .sort((x, y) => x.positionFromM - y.positionFromM);
}

export interface Totals {
  vBest: number;
  vMin: number;
  vMax: number;
  tonnes: number;
  stacked: number;
  reclaimed: number;
}

/** Σ over the rows (the totals line must equal the sum of what is shown). */
export function totals(rows: PileRow[]): Totals {
  const sum = (k: keyof Totals) => rows.reduce((s, r) => s + ((r[k] as number | null) ?? 0), 0);
  return {
    vBest: sum("vBest"),
    vMin: sum("vMin"),
    vMax: sum("vMax"),
    tonnes: sum("tonnes"),
    stacked: sum("stacked"),
    reclaimed: sum("reclaimed"),
  };
}

export const fmt0 = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : (Math.round(v) + 0).toLocaleString("en-US"); // + 0: no "-0"
export const fmtPct = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${Math.round(v * 100)} %`;
export const fmtSigned = (v: number) => (Math.round(v) === 0 ? "0" : `${v > 0 ? "+" : "−"}${fmt0(Math.abs(v))}`);

/** A volume range for display: a lower bound below zero (a barely seen area) reads as 0. */
export const fmtRange = (lo: number | null | undefined, hi: number | null | undefined) =>
  `${fmt0(lo === null || lo === undefined ? lo : Math.max(0, lo))}–${fmt0(hi)}`;
